import classnames from 'classnames'
import style from './style.module.scss'
import { ButtonLink, Header, Text } from '@crossfit/design-system'
import { useIsTabletSmallOrAbove } from 'hooks/useMediaQuery'
import { Link } from 'react-router-dom'
import { Icon } from '@crossfit/ui'
import { Role, User } from 'types'
import { useSelector } from 'react-redux'
import { getBanner, getMyAffiliates, getUser } from 'store/selectors'
import { useAuth } from 'components/Auth'

type Props = {
  onClose: () => void
  isLoggedIn: boolean
  loginButtonText: string
  viewProfileText: string
  logoutText: string
  isMobile?: boolean
}

const AFFILIATE_DASHBOARD = 'Affiliate Dashboard'
const AFFILIATE_TOOLKIT = 'Affiliate Toolkit'
const AFFILIATE_MANAGEMENT = 'Affiliate Management'
const COACHES_AND_STAFF = 'Coaches & Staff'
const AFFILIATE_PORTAL = 'Affiliate Portal'
const PERSONAL = 'Personal'
const MY_ACCOUNT = 'My Account'
const COMP_DASHBOARD = 'Competition Dashboard'

function AccountMenu({
  onClose,
  isLoggedIn,
  logoutText,
  isMobile = false,
}: Props) {
  const user = useSelector(getUser) as User
  const isTabletSmallOrAbove = useIsTabletSmallOrAbove()
  const { selectedAffiliate } = useSelector(getMyAffiliates)
  const { showBanner } = useSelector(getBanner)
  const { logout } = useAuth()

  return (
    <ul
      aria-label='menu'
      className={classnames(
        style.wrapper,
        showBanner && style.showingBanner,
        isMobile ? style.mobile : style.desktop,
      )}
    >
      <div className={style.heading} onClick={onClose}>
        <Header
          as='h2'
          size='xs'
        >{`Welcome, ${user?.firstName} ${user?.lastName}`}</Header>
        {!isTabletSmallOrAbove && (
          <Icon name='chevron' className={style.icon} />
        )}
      </div>

      <li aria-label='affiliate-toolkit-links' className={style.links}>
        <Text as='label' className={style.label} type='overline' size='xs' bold>
          {AFFILIATE_TOOLKIT}
        </Text>
        <ul>
          <li>
            <Link className={style.link} to='/' onClick={onClose}>
              <Text as='span' type='body' size='sm'>
                {AFFILIATE_DASHBOARD}
              </Text>
            </Link>
          </li>

          <li>
            <Link
              className={style.link}
              to='/admin/directory-page'
              onClick={onClose}
            >
              <Text as='span' type='body' size='sm'>
                {(selectedAffiliate?.roles as Role[])?.some((r) =>
                  [Role.Manager, Role.LOR].includes(r),
                )
                  ? AFFILIATE_MANAGEMENT
                  : COACHES_AND_STAFF}
              </Text>
            </Link>
          </li>
          {(selectedAffiliate?.roles as Role[])?.some((r) =>
            [Role.Manager, Role.LOR].includes(r),
          ) && (
            <li>
              <Link
                className={style.link}
                to='https://affiliate.crossfit.com/affiliate-portal/affiliateprofile'
                onClick={onClose}
              >
                <Text as='span' type='body' size='sm'>
                  {AFFILIATE_PORTAL}
                </Text>
              </Link>
            </li>
          )}
        </ul>
      </li>

      <li aria-label='personal-links' className={style.links}>
        {isLoggedIn ? (
          <Text
            as='label'
            className={style.label}
            type='overline'
            size='xs'
            bold
          >
            {PERSONAL}
          </Text>
        ) : null}
      </li>
      <li>
        <Link
          className={style.link}
          to='https://crossfit.com/dashboard'
          onClick={onClose}
        >
          <Text as='span' type='body' size='sm'>
            {MY_ACCOUNT}
          </Text>
        </Link>
      </li>
      <li>
        <Link
          className={style.link}
          to='https://games.crossfit.com/manage-competition/athlete'
          onClick={onClose}
        >
          <Text as='span' type='body' size='sm'>
            {COMP_DASHBOARD}
          </Text>
        </Link>
      </li>
      <li aria-label='logout-link' className={style.links}>
        <Link
          className={classnames(style.link, style.logout)}
          to='/logout'
          onClick={logout}
        >
          <ButtonLink
            variant='secondary'
            color='white'
            size='sm'
            href='/logout'
          >
            {logoutText}
          </ButtonLink>
        </Link>
      </li>
    </ul>
  )
}

export default AccountMenu
